import { FC, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Col, Row } from 'react-bootstrap';
import { Bookmark, BookmarkFill, Copy, Download, FileEarmarkText, Tags } from 'react-bootstrap-icons';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';

import { AccountBookmarkModel, BOOKMARK_TYPE } from '@/lib/models';
import { accountService, documentService } from '@/lib/services';
import {
	createUseThemedStyles,
	useAccount,
	useAlert,
	useAnalytics,
	useCopyTextToClipboard,
	useHandleError,
	useNavigationConfig,
} from '@/hooks';

import { ReactComponent as ChevronIcon } from '@/assets/icons/icon-chevron-right.svg';

const useStyles = createUseThemedStyles((theme) => ({
	item: {
		padding: '24px 0',
		borderBottom: `1px solid ${theme.colors.gray300}`,
	},
	chevronIcon: {
		marginTop: -3,
		marginLeft: 5,
	},
}));

interface SearchListItemProps {
	id: string;
	tags?: string;
	category: string;
	title: string;
	description: string;
	detailsUrl: string;
	downloadUrl?: string;
	viewUrl?: string;
	canDownload?: boolean;
	type: string;
	isBookmarked: boolean;
	bookmarkType?: BOOKMARK_TYPE;
	onChange(bookmark: AccountBookmarkModel): void;
	isPodcast?: boolean;
}

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

export const SearchListItem: FC<SearchListItemProps> = ({
	id,
	tags,
	category,
	title,
	description,
	detailsUrl,
	downloadUrl,
	viewUrl,
	canDownload,
	type,
	isBookmarked,
	bookmarkType,
	onChange,
	isPodcast,
}) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const { account } = useAccount();
	const { copyTextToClipboard } = useCopyTextToClipboard();
	const { navigationConfig } = useNavigationConfig();
	const handleError = useHandleError();
	const { showAlert } = useAlert();
	const [isBookmarking, setIsBookmarking] = useState(false);
	const analytics = useAnalytics();
	const handleShareButtonClick = () => {
		copyTextToClipboard(`${window.location.origin}/${detailsUrl}`, 'Link copied to clipboard.');
	};

	const handleBookmarkButtonClick = useCallback(
		async (active: boolean, message: string) => {
			if (!bookmarkType) {
				return;
			}

			setIsBookmarking(true);

			try {
				if (!account) {
					throw new Error('account is undefined.');
				}

				const { bookmark } = await accountService
					.updateAccountBookmarkContent(account.accountId, {
						bookmarkType,
						contentId: id,
						activeFlag: active,
					})
					.fetch();

				onChange?.(bookmark);

				showAlert({
					variant: 'success',
					children: <p className="mb-0 text-white">{message}</p>,
				});
			} catch (error) {
				handleError(error);
			} finally {
				setIsBookmarking(false);
			}
		},
		[account, bookmarkType, handleError, id, onChange, showAlert]
	);

	const handleViewClick = useCallback(async () => {
		if (!viewUrl) {
			return;
		}

		try {
			if (!isPodcast) {
				await documentService.trackDocumentView(id).fetch();
			}
		} catch (error) {
			// fail silently because we dont need to report a failed analytics call
		} finally {
			if (isPodcast) {
				navigate(detailsUrl);
			} else {
				analytics.handleClickEvent(viewUrl);
				window.open(viewUrl, '_blank', 'noopener');
			}
		}
	}, [analytics, detailsUrl, id, isPodcast, navigate, viewUrl]);

	const handleDownloadClick = useCallback(async () => {
		if (downloadUrl) {
			try {
				await documentService.trackDocumentDownload(id).fetch();
				analytics.handleClickEvent(downloadUrl);
			} catch (error) {
				// fail silently because we dont need to report a failed analytics call
			} finally {
				window.open(downloadUrl, '_blank', 'noopener');
			}
		}
	}, [analytics, downloadUrl, id]);

	return (
		<>
			<div className={classes.item}>
				<Row className="mb-1">
					<Col>
						<div className="mb-2 d-flex align-items-center justify-content-between">
							<h4 className="mb-0 d-flex align-items-center text-muted">{category}</h4>
							<div className="d-flex align-items-center text-primary">
								{viewUrl && (
									<Button variant="link" className="mr-1" onClick={handleViewClick}>
										<FileEarmarkText />
									</Button>
								)}
								{navigationConfig?.allowDownloads && canDownload && downloadUrl && (
									<Button className="mr-1" variant="link" onClick={handleDownloadClick}>
										<Download />
									</Button>
								)}
								<Button variant="link" className="mx-1" onClick={handleShareButtonClick}>
									<Copy />
								</Button>
								{(type === 'document' || type === 'blog') && (
									<>
										{isBookmarked ? (
											<Button
												variant="link"
												className="ml-1"
												onClick={() => {
													handleBookmarkButtonClick(false, 'Bookmark removed.');
												}}
												disabled={isBookmarking}
											>
												<BookmarkFill />
											</Button>
										) : (
											<Button
												variant="link"
												className="ml-1"
												onClick={() => {
													handleBookmarkButtonClick(true, 'Bookmark saved.');
												}}
												disabled={isBookmarking}
											>
												<Bookmark />
											</Button>
										)}
									</>
								)}
							</div>
						</div>
						{title ? (
							<ResponsiveEllipsis text={title ?? ''} maxLine="2" component="h3" />
						) : (
							<ResponsiveEllipsis text={description ?? ''} maxLine="2" component="p" />
						)}
					</Col>
				</Row>

				{title && description && (
					<Row className="mb-2">
						<Col>
							<ResponsiveEllipsis text={description} maxLine="2" component="p" />
						</Col>
					</Row>
				)}

				<Row>
					<Col>
						<div className="d-flex align-items-start justify-content-between">
							<div>
								{tags && (
									<>
										<Tags className="text-primary" />
										<span className="small font-weight-semi-bold text-muted ml-2">{tags}</span>
									</>
								)}
							</div>
							{detailsUrl && (
								<div className="d-flex align-items-start justify-content-between text-primary ml-4">
									<strong>
										<Link to={`/${detailsUrl}`} className="text-nowrap">
											View Details <ChevronIcon className={classes.chevronIcon} />
										</Link>
									</strong>
								</div>
							)}
						</div>
					</Col>
				</Row>
			</div>
		</>
	);
};
